import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import KeyIcon from "@mui/icons-material/Key";
import LinkIcon from "@mui/icons-material/Link";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsIcon from "@mui/icons-material/Notifications";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import SmsIcon from "@mui/icons-material/Sms";
import TelegramIcon from "@mui/icons-material/Telegram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import { API_URL, API_UPLOAD_URL } from "./config";
import {
  getCampusFromToken,
  getAuthHeaders,
} from "./commonfunctions/useAuthHelpers";

const NOTIFICATION_TABS = [
  {
    key: "providers",
    label: "Provider Configurations",
    icon: <SettingsIcon />,
  },
  {
    key: "preferences",
    label: "Channel Preferences",
    icon: <NotificationsActiveIcon />,
  },
];

const CHANNEL_OPTIONS = [
  {
    value: "telegram",
    label: "Telegram",
    icon: <TelegramIcon fontSize="small" />,
  },
  {
    value: "whatsapp",
    label: "WhatsApp",
    icon: <WhatsAppIcon fontSize="small" />,
  },
  {
    value: "sms",
    label: "SMS",
    icon: <SmsIcon fontSize="small" />,
  },
  {
    value: "email",
    label: "Email",
    icon: <EmailIcon fontSize="small" />,
  },
];

const PREFERENCE_CHANNELS = [
  {
    key: "telegram",
    label: "Telegram",
    icon: <TelegramIcon fontSize="small" />,
  },
  {
    key: "email",
    label: "Email",
    icon: <EmailIcon fontSize="small" />,
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    icon: <WhatsAppIcon fontSize="small" />,
  },
  {
    key: "sms",
    label: "SMS",
    icon: <SmsIcon fontSize="small" />,
  },
  {
    key: "push",
    label: "Push",
    icon: <NotificationsIcon fontSize="small" />,
  },
];

const PROVIDERS_BY_CHANNEL = {
  telegram: [{ value: "TELEGRAM_BOT", label: "Telegram Bot" }],
  whatsapp: [
    { value: "META_CLOUD_API", label: "Meta WhatsApp Cloud API" },
    { value: "GUPSHUP", label: "Gupshup" },
    { value: "TWILIO", label: "Twilio" },
    { value: "CUSTOM", label: "Custom" },
  ],
  sms: [
    { value: "MSG91", label: "MSG91" },
    { value: "TEXTLOCAL", label: "Textlocal" },
    { value: "TWILIO", label: "Twilio" },
    { value: "CUSTOM", label: "Custom" },
  ],
  email: [
    { value: "SMTP", label: "SMTP" },
    { value: "SENDGRID", label: "SendGrid" },
    { value: "MAILGUN", label: "Mailgun" },
    { value: "AWS_SES", label: "AWS SES" },
    { value: "CUSTOM", label: "Custom" },
  ],
};

const getProviderOptions = (channel) =>
  PROVIDERS_BY_CHANNEL[String(channel || "").toLowerCase()] || [];

const getDefaultProvider = (channel) =>
  getProviderOptions(channel)?.[0]?.value || "";

const getDefaultApiBaseUrl = (channel, provider) => {
  const c = String(channel || "").toLowerCase();
  const p = String(provider || "").toUpperCase();

  if (c === "telegram") return "https://api.telegram.org";
  if (c === "whatsapp" && p === "META_CLOUD_API")
    return "https://graph.facebook.com";
  if (c === "whatsapp" && p === "GUPSHUP")
    return "https://api.gupshup.io";
  if (c === "whatsapp" && p === "TWILIO")
    return "https://api.twilio.com";
  if (c === "sms" && p === "MSG91")
    return "https://control.msg91.com";
  if (c === "sms" && p === "TEXTLOCAL")
    return "https://api.textlocal.in";
  if (c === "sms" && p === "TWILIO")
    return "https://api.twilio.com";
  if (c === "email" && p === "SENDGRID")
    return "https://api.sendgrid.com/v3";
  if (c === "email" && p === "MAILGUN")
    return "https://api.mailgun.net";
  return "";
};

const buildEmptyProvider = () => {
  const Channel = "telegram";
  const ProviderCode = getDefaultProvider(Channel);

  return {
    SubCampusCode: "",
    Channel,
    ProviderCode,
    DisplayName: "",
    ApiBaseUrl: getDefaultApiBaseUrl(Channel, ProviderCode),
    ApiKey: "",
    ApiSecret: "",
    SenderId: "",
    TelegramBotUsername: "",
    TelegramWebhookSecret: "",
    TelegramAlertsChatId: "",
    TelegramNotificationsChatId: "",
    TelegramAlertsInviteLink: "",
    TelegramNotificationsInviteLink: "",
    FromEmail: "",
    FromName: "",
    ExtraConfigJson: "",
    IsActive: 1,
    IsDefault: 0,
  };
};

export default function NotificationSetup() {
  // Theme copied from StudentFeeSetup / AcademicCodeSetup.
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const BLUE = "#1565C0";

  const [tab, setTab] = useState(0);
  const activeTab = NOTIFICATION_TABS[tab];

  const [campusCode, setCampusCode] = useState("");
  const [subCampusCodes, setSubCampusCodes] = useState([]);
  const [setupGuides, setSetupGuides] = useState([]);
  const [msg, setMsg] = useState({ type: "success", text: "" });

  // Provider Config state
  const [providerRows, setProviderRows] = useState([]);
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [providerMode, setProviderMode] = useState("add");
  const [providerDraft, setProviderDraft] = useState(buildEmptyProvider());
  const [providerPage, setProviderPage] = useState(0);
  const [providerRowsPerPage, setProviderRowsPerPage] = useState(25);
  const [providerLoading, setProviderLoading] = useState(false);
  const [providerSaving, setProviderSaving] = useState(false);

  // Preferences state
  const [preferenceRows, setPreferenceRows] = useState([]);
  const [preferencePage, setPreferencePage] = useState(0);
  const [preferenceRowsPerPage, setPreferenceRowsPerPage] = useState(25);
  const [preferenceLoading, setPreferenceLoading] = useState(false);
  const [preferenceSaving, setPreferenceSaving] = useState(false);

  const pageWrapSx = {
    p: 2,
    backgroundColor: BG,
    minHeight: "100vh",
  };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
  };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg,${THEME_ORANGE},${THEME_ORANGE_2})`,
    },
  };

  const popupSectionSx = {
    ...surfaceSx,
    p: 2.5,
    borderRadius: 2.5,
    borderTop: `4px solid ${THEME_ORANGE}`,
    overflow: "hidden",
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: BORDER_HOVER },
      "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: THEME_ORANGE,
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 800,
    borderColor: "rgba(25,118,210,0.35)",
    color: BLUE,
    backgroundColor: "white",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    color: BLUE,
    "&:hover": {
      backgroundColor: "rgba(240,120,40,0.10)",
      color: THEME_ORANGE,
    },
  };

  const dangerIconBtnSx = {
    color: "rgba(244,67,54,0.90)",
    "&:hover": {
      backgroundColor: "rgba(244,67,54,0.08)",
      color: "rgba(244,67,54,1)",
    },
  };

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      whiteSpace: "nowrap",
      borderBottom: "1px solid rgba(255,255,255,0.18)",
    },
  };

  const zebraRowSx = (index) => ({
    backgroundColor: index % 2 === 0 ? ROW_ALT : "#fff",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": {
      borderBottom: "1px solid rgba(25,118,210,0.10)",
    },
  });

  const tabsSx = {
    px: 1,
    borderBottom: `1px solid ${BORDER}`,
    minHeight: 48,
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 800,
      minHeight: 48,
    },
    "& .Mui-selected": {
      color: `${THEME_ORANGE} !important`,
    },
    "& .MuiTabs-indicator": {
      backgroundColor: THEME_ORANGE,
      height: 3,
    },
  };

  const serialBadge = (number) => (
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 900,
        fontSize: "1rem",
        color: "#1976D2",
        backgroundColor: "rgba(25,118,210,0.06)",
        border: "2px solid rgba(25,118,210,0.20)",
        lineHeight: 1,
      }}
    >
      {number}
    </Box>
  );

  useEffect(() => {
    setCampusCode(getCampusFromToken?.() || "");
  }, []);

  const getAuthConfig = () => ({
    headers: getAuthHeaders?.(),
  });

  const getProviderRowId = (row) =>
    row.NotifyProviderConfigID || row.NotifyConfigId || row.id;

  const getChannelIcon = (channel) => {
    switch (String(channel || "").toLowerCase()) {
      case "telegram":
        return <TelegramIcon fontSize="small" />;
      case "whatsapp":
        return <WhatsAppIcon fontSize="small" />;
      case "sms":
        return <SmsIcon fontSize="small" />;
      case "email":
        return <EmailIcon fontSize="small" />;
      default:
        return <NotificationsActiveIcon fontSize="small" />;
    }
  };

  const getGuideUrl = (guideUrl) => {
    if (!guideUrl) return "";
    const clean = String(guideUrl).trim();

    if (
      clean.startsWith("http://") ||
      clean.startsWith("https://") ||
      clean.startsWith("data:")
    ) {
      return clean;
    }

    return `${API_UPLOAD_URL}/uploads/${clean}`;
  };

  const getGuideForChannel = (channel) => {
    const selectedFeature = String(channel || "").toUpperCase();
    return setupGuides.find(
      (guide) =>
        String(guide.FeatureCode || "").toUpperCase() === selectedFeature
    );
  };

  const openSetupGuide = (channel) => {
    const guide = getGuideForChannel(channel);

    if (!guide) {
      setMsg({
        type: "error",
        text: `No setup guide found for ${channel || "this method"}.`,
      });
      return;
    }

    const url = getGuideUrl(guide.GuideUrl);

    if (!url) {
      setMsg({ type: "error", text: "Guide PDF link is missing." });
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (!campusCode) return;

    axios
      .get(`${API_URL}/api/ValidateCampus/${campusCode}`, getAuthConfig())
      .then((res) => {
        setSubCampusCodes(res.data?.subcampuses || []);
      })
      .catch(() => {
        setMsg({ type: "error", text: "Failed to fetch subcampuses." });
      });

    axios
      .get(`${API_URL}/api/setup-guides`, {
        headers: getAuthHeaders?.(),
        params: { moduleCode: "NOTIFICATIONS" },
      })
      .then((res) => setSetupGuides(res.data || []))
      .catch(() => {
        setMsg({ type: "error", text: "Failed to load setup guides." });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode]);

  // ============================================================
  // PROVIDER CONFIGURATIONS
  // ============================================================

  const fetchProviders = async () => {
    setProviderLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/notification-provider-configs/list`,
        getAuthConfig()
      );
      setProviderRows(res.data || []);
    } catch (err) {
      setMsg({
        type: "error",
        text:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to load notification provider configurations.",
      });
    } finally {
      setProviderLoading(false);
    }
  };

  useEffect(() => {
    if (campusCode) fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode]);

  const validateExtraJson = (value) => {
    if (!String(value || "").trim()) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  };

  const updateProviderDraft = (name, value, isCheckbox = false) => {
    setProviderDraft((prev) => {
      if (name === "Channel") {
        const ProviderCode = getDefaultProvider(value);
        return {
          ...prev,
          Channel: value,
          ProviderCode,
          ApiBaseUrl: getDefaultApiBaseUrl(value, ProviderCode),
          FromEmail: value === "email" ? prev.FromEmail : "",
          FromName: value === "email" ? prev.FromName : "",
        };
      }

      if (name === "ProviderCode") {
        return {
          ...prev,
          ProviderCode: value,
          ApiBaseUrl: getDefaultApiBaseUrl(prev.Channel, value),
        };
      }

      return {
        ...prev,
        [name]: isCheckbox ? (value ? 1 : 0) : value,
      };
    });
  };

  const openAddProvider = () => {
    setProviderMode("add");
    setProviderDraft(buildEmptyProvider());
    setProviderDialogOpen(true);
  };

  const openEditProvider = (row) => {
    setProviderMode("edit");
    setProviderDraft({
      ...buildEmptyProvider(),
      ...row,
      NotifyProviderConfigID: getProviderRowId(row),
      ApiKey: "",
      ApiSecret: "",
    });
    setProviderDialogOpen(true);
  };

  const saveProvider = async () => {
    if (!providerDraft.Channel) {
      return setMsg({ type: "error", text: "Please select channel." });
    }

    if (!providerDraft.ProviderCode) {
      return setMsg({ type: "error", text: "Please select provider." });
    }

    if (!validateExtraJson(providerDraft.ExtraConfigJson)) {
      return setMsg({
        type: "error",
        text: "Extra Config JSON is invalid.",
      });
    }

    setProviderSaving(true);
    try {
      if (providerMode === "add") {
        await axios.post(
          `${API_URL}/api/notification-provider-configs/list`,
          providerDraft,
          getAuthConfig()
        );
      } else {
        const id =
          providerDraft.NotifyProviderConfigID ||
          providerDraft.NotifyConfigId ||
          providerDraft.id;

        await axios.put(
          `${API_URL}/api/notification-provider-configs/list/${id}`,
          providerDraft,
          getAuthConfig()
        );
      }

      setMsg({
        type: "success",
        text:
          providerMode === "add"
            ? "Notification provider added successfully."
            : "Notification provider updated successfully.",
      });

      setProviderDialogOpen(false);
      setProviderDraft(buildEmptyProvider());
      await fetchProviders();
    } catch (err) {
      setMsg({
        type: "error",
        text:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to save notification provider.",
      });
    } finally {
      setProviderSaving(false);
    }
  };

  const deleteProvider = async (row) => {
    const id = getProviderRowId(row);
    if (!id) return;

    if (
      !window.confirm(
        `Delete notification provider "${row.DisplayName || row.ProviderCode}"?`
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/notification-provider-configs/list/${id}`,
        getAuthConfig()
      );
      setMsg({
        type: "success",
        text: "Notification provider deleted successfully.",
      });
      await fetchProviders();
    } catch (err) {
      setMsg({
        type: "error",
        text:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to delete notification provider.",
      });
    }
  };

  const getApiPublicBase = () => {
    const configured = String(API_URL || "").replace(/\/+$/, "");

    if (
      configured.startsWith("http://") ||
      configured.startsWith("https://")
    ) {
      return configured;
    }

    return window.location.origin;
  };

  const getTelegramWebhookUrl = (row, rowId) => {
    const secret =
      row.TelegramWebhookSecret || row.WebhookSecret || "";
    if (!rowId || !secret) return "";

    return `${getApiPublicBase()}/api/telegram/webhook/${rowId}/${encodeURIComponent(
      secret
    )}`;
  };

  const registerTelegramWebhook = (row) => {
    const rowId = getProviderRowId(row);

    if (row.Channel !== "telegram") {
      setMsg({
        type: "error",
        text: "Webhook registration is only for Telegram providers.",
      });
      return;
    }

    const botToken = row.ApiKey || "";

    if (!botToken || botToken.includes("*")) {
      setMsg({
        type: "error",
        text:
          "Bot token is not available here. Edit the Telegram provider and enter the Bot Token again before registering the webhook.",
      });
      return;
    }

    const webhookUrl = getTelegramWebhookUrl(row, rowId);

    if (!webhookUrl) {
      setMsg({
        type: "error",
        text: "Provider ID or Telegram Webhook Secret is missing.",
      });
      return;
    }

    const registerUrl = `https://api.telegram.org/bot${botToken}/setWebhook?url=${encodeURIComponent(
      webhookUrl
    )}`;

    window.open(registerUrl, "_blank", "noopener,noreferrer");
  };

  const providerMaxPage = Math.max(
    0,
    Math.ceil(providerRows.length / providerRowsPerPage) - 1
  );
  const safeProviderPage = Math.min(providerPage, providerMaxPage);
  const paginatedProviders = providerRows.slice(
    safeProviderPage * providerRowsPerPage,
    safeProviderPage * providerRowsPerPage + providerRowsPerPage
  );

  useEffect(() => {
    if (providerPage > providerMaxPage) {
      setProviderPage(providerMaxPage);
    }
  }, [providerPage, providerMaxPage]);

  // ============================================================
  // CHANNEL PREFERENCES
  // ============================================================

  const fetchPreferences = async () => {
    setPreferenceLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/notification-channel-preferences/list`,
        getAuthConfig()
      );
      setPreferenceRows(res.data || []);
    } catch (err) {
      setMsg({
        type: "error",
        text:
          err?.response?.data?.message ||
          "Failed to load notification channel preferences.",
      });
    } finally {
      setPreferenceLoading(false);
    }
  };

  useEffect(() => {
    if (campusCode) fetchPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode]);

  const updatePreferenceChannel = (
    eventCode,
    channelKey,
    fieldName
  ) => {
    setPreferenceRows((prev) =>
      prev.map((row) => {
        if (row.EventCode !== eventCode) return row;

        const old = row.channels?.[channelKey] || {};

        return {
          ...row,
          channels: {
            ...row.channels,
            [channelKey]: {
              ...old,
              [fieldName]: old[fieldName] ? 0 : 1,
            },
          },
        };
      })
    );
  };

  const seedMissingPreferences = async () => {
    try {
      await axios.post(
        `${API_URL}/api/notification-channel-preferences/seed-missing`,
        {},
        getAuthConfig()
      );
      setMsg({
        type: "success",
        text: "Missing notification preferences seeded successfully.",
      });
      await fetchPreferences();
    } catch (err) {
      setMsg({
        type: "error",
        text:
          err?.response?.data?.message ||
          "Failed to seed missing preferences.",
      });
    }
  };

  const savePreferences = async () => {
    setPreferenceSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/notification-channel-preferences/bulk-save`,
        { rows: preferenceRows },
        getAuthConfig()
      );
      setMsg({
        type: "success",
        text: "Notification channel preferences saved successfully.",
      });
      await fetchPreferences();
    } catch (err) {
      setMsg({
        type: "error",
        text:
          err?.response?.data?.message ||
          "Failed to save notification channel preferences.",
      });
    } finally {
      setPreferenceSaving(false);
    }
  };

  const preferenceMaxPage = Math.max(
    0,
    Math.ceil(preferenceRows.length / preferenceRowsPerPage) - 1
  );
  const safePreferencePage = Math.min(
    preferencePage,
    preferenceMaxPage
  );
  const paginatedPreferences = preferenceRows.slice(
    safePreferencePage * preferenceRowsPerPage,
    safePreferencePage * preferenceRowsPerPage +
      preferenceRowsPerPage
  );

  useEffect(() => {
    if (preferencePage > preferenceMaxPage) {
      setPreferencePage(preferenceMaxPage);
    }
  }, [preferencePage, preferenceMaxPage]);

  // ============================================================
  // PROVIDER FORM
  // ============================================================

  const renderProviderForm = () => (
    <Stack spacing={2}>
      <Paper elevation={0} sx={popupSectionSx}>
        <Typography sx={{ fontWeight: 900, color: BLUE, mb: 0.4 }}>
          Provider Details
        </Typography>
        <Typography
          sx={{
            fontSize: "0.82rem",
            color: "text.secondary",
            mb: 2,
          }}
        >
          Configure the notification method, provider credentials and sender details.
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(220px, 1fr))",
              lg: "repeat(3, minmax(220px, 1fr))",
            },
            gap: 2,
          }}
        >
          <TextField
            select
            label="Sub Campus"
            value={providerDraft.SubCampusCode || ""}
            onChange={(e) =>
              updateProviderDraft("SubCampusCode", e.target.value)
            }
            size="small"
            fullWidth
            sx={inputSx}
            helperText="Leave blank if config applies to the entire campus"
          >
            <MenuItem value="">Campus Default</MenuItem>
            {subCampusCodes.map((sc) => (
              <MenuItem
                key={sc.SubCampusCode}
                value={sc.SubCampusCode}
              >
                {sc.SubCampusCode}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Notification Method"
            value={providerDraft.Channel || ""}
            onChange={(e) =>
              updateProviderDraft("Channel", e.target.value)
            }
            size="small"
            fullWidth
            sx={inputSx}
          >
            {CHANNEL_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {option.icon}
                  {option.label}
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Provider"
            value={providerDraft.ProviderCode || ""}
            onChange={(e) =>
              updateProviderDraft("ProviderCode", e.target.value)
            }
            size="small"
            fullWidth
            sx={inputSx}
          >
            {getProviderOptions(providerDraft.Channel).map(
              (provider) => (
                <MenuItem
                  key={provider.value}
                  value={provider.value}
                >
                  {provider.label}
                </MenuItem>
              )
            )}
          </TextField>

          <TextField
            label="Display Name"
            value={providerDraft.DisplayName || ""}
            onChange={(e) =>
              updateProviderDraft("DisplayName", e.target.value)
            }
            size="small"
            fullWidth
            sx={inputSx}
            placeholder="Example: KIET WhatsApp Gupshup"
          />

          <TextField
            label="API Base URL / SMTP Host"
            value={providerDraft.ApiBaseUrl || ""}
            onChange={(e) =>
              updateProviderDraft("ApiBaseUrl", e.target.value)
            }
            size="small"
            fullWidth
            sx={inputSx}
            InputProps={{
              startAdornment: (
                <LinkIcon
                  fontSize="small"
                  sx={{ mr: 1, color: "rgba(25,118,210,0.8)" }}
                />
              ),
            }}
          />

          <TextField
            label="API Key / Token / Username"
            value={providerDraft.ApiKey || ""}
            onChange={(e) =>
              updateProviderDraft("ApiKey", e.target.value)
            }
            size="small"
            fullWidth
            type="password"
            sx={inputSx}
            placeholder={
              providerMode === "edit"
                ? "Enter only to replace existing value"
                : ""
            }
            InputProps={{
              startAdornment: (
                <KeyIcon
                  fontSize="small"
                  sx={{ mr: 1, color: "rgba(25,118,210,0.8)" }}
                />
              ),
            }}
          />

          <TextField
            label="API Secret / Password"
            value={providerDraft.ApiSecret || ""}
            onChange={(e) =>
              updateProviderDraft("ApiSecret", e.target.value)
            }
            size="small"
            fullWidth
            type="password"
            sx={inputSx}
            placeholder={
              providerMode === "edit"
                ? "Enter only to replace existing value"
                : ""
            }
          />

          <TextField
            label="Sender ID / WhatsApp Number"
            value={providerDraft.SenderId || ""}
            onChange={(e) =>
              updateProviderDraft("SenderId", e.target.value)
            }
            size="small"
            fullWidth
            sx={inputSx}
            placeholder="SMS sender id / WhatsApp phone number id"
          />

          <TextField
            label="From Email"
            value={providerDraft.FromEmail || ""}
            onChange={(e) =>
              updateProviderDraft("FromEmail", e.target.value)
            }
            size="small"
            fullWidth
            sx={inputSx}
            disabled={providerDraft.Channel !== "email"}
          />

          <TextField
            label="From Name"
            value={providerDraft.FromName || ""}
            onChange={(e) =>
              updateProviderDraft("FromName", e.target.value)
            }
            size="small"
            fullWidth
            sx={inputSx}
            disabled={providerDraft.Channel !== "email"}
          />

          <TextField
            label="Extra Config JSON"
            value={providerDraft.ExtraConfigJson || ""}
            onChange={(e) =>
              updateProviderDraft("ExtraConfigJson", e.target.value)
            }
            size="small"
            fullWidth
            multiline
            minRows={1}
            sx={inputSx}
            placeholder='{"phoneNumberId":"...","smtpPort":587}'
          />

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ px: 0.5 }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={Boolean(providerDraft.IsActive)}
                onChange={(e) =>
                  updateProviderDraft(
                    "IsActive",
                    e.target.checked,
                    true
                  )
                }
                sx={checkboxSx}
              />
              <Typography variant="body2">Active</Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={Boolean(providerDraft.IsDefault)}
                onChange={(e) =>
                  updateProviderDraft(
                    "IsDefault",
                    e.target.checked,
                    true
                  )
                }
                sx={checkboxSx}
              />
              <Typography variant="body2">Default</Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {providerDraft.Channel === "telegram" && (
        <Paper elevation={0} sx={popupSectionSx}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography sx={{ fontWeight: 900, color: BLUE }}>
                Telegram Configuration
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  color: "text.secondary",
                }}
              >
                Configure bot username, webhook security, channel IDs and invite links.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<MenuBookIcon />}
              sx={outlinedBtnSx}
              onClick={() => openSetupGuide("telegram")}
            >
              Setup Guide
            </Button>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(220px, 1fr))",
              },
              gap: 2,
            }}
          >
            <TextField
              label="Telegram Bot Username"
              value={providerDraft.TelegramBotUsername || ""}
              onChange={(e) =>
                updateProviderDraft(
                  "TelegramBotUsername",
                  e.target.value
                )
              }
              size="small"
              fullWidth
              sx={inputSx}
              placeholder="UniFLOO_Notifications_Bot"
            />

            <TextField
              label="Telegram Webhook Secret"
              value={providerDraft.TelegramWebhookSecret || ""}
              onChange={(e) =>
                updateProviderDraft(
                  "TelegramWebhookSecret",
                  e.target.value
                )
              }
              size="small"
              fullWidth
              sx={inputSx}
              placeholder="Random string to secure Telegram webhooks"
            />

            <TextField
              label="Telegram Alerts Chat ID"
              value={providerDraft.TelegramAlertsChatId || ""}
              onChange={(e) =>
                updateProviderDraft(
                  "TelegramAlertsChatId",
                  e.target.value
                )
              }
              size="small"
              fullWidth
              sx={inputSx}
              placeholder="-100xxxxxxxxxx"
            />

            <TextField
              label="Telegram Notifications Chat ID"
              value={providerDraft.TelegramNotificationsChatId || ""}
              onChange={(e) =>
                updateProviderDraft(
                  "TelegramNotificationsChatId",
                  e.target.value
                )
              }
              size="small"
              fullWidth
              sx={inputSx}
              placeholder="-100xxxxxxxxxx"
            />

            <TextField
              label="Telegram Alerts Invite Link"
              value={providerDraft.TelegramAlertsInviteLink || ""}
              onChange={(e) =>
                updateProviderDraft(
                  "TelegramAlertsInviteLink",
                  e.target.value
                )
              }
              size="small"
              fullWidth
              sx={inputSx}
              placeholder="https://t.me/+..."
            />

            <TextField
              label="Telegram Notifications Invite Link"
              value={
                providerDraft.TelegramNotificationsInviteLink || ""
              }
              onChange={(e) =>
                updateProviderDraft(
                  "TelegramNotificationsInviteLink",
                  e.target.value
                )
              }
              size="small"
              fullWidth
              sx={inputSx}
              placeholder="https://t.me/+..."
            />
          </Box>
        </Paper>
      )}
    </Stack>
  );

  // ============================================================
  // PROVIDER TAB
  // ============================================================

  const renderProviders = () => (
    <Stack spacing={2}>
      <Paper elevation={0} sx={sectionSx}>
        <Typography sx={{ fontWeight: 900, color: BLUE }}>
          Provider Configurations
        </Typography>
        <Typography
          sx={{
            mt: 0.25,
            fontSize: "0.85rem",
            color: "text.secondary",
          }}
        >
          Setup Telegram, WhatsApp, SMS and Email providers for this campus.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 1500 }}>
              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell>#</TableCell>
                  <TableCell>Campus</TableCell>
                  <TableCell>Sub Campus</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>API Base URL</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell>API Secret</TableCell>
                  <TableCell>Sender</TableCell>
                  <TableCell>From Email</TableCell>
                  <TableCell>From Name</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedProviders.map((row, index) => {
                  const rowId = getProviderRowId(row);
                  const serial =
                    safeProviderPage * providerRowsPerPage +
                    index +
                    1;

                  return (
                    <TableRow
                      key={rowId ?? `provider-${index}`}
                      sx={zebraRowSx(index)}
                    >
                      <TableCell>{serialBadge(serial)}</TableCell>
                      <TableCell>{row.CampusCode || campusCode}</TableCell>
                      <TableCell>
                        {row.SubCampusCode || "Campus Default"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getChannelIcon(row.Channel)}
                          label={row.Channel}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            color: BLUE,
                            backgroundColor:
                              "rgba(25,118,210,0.08)",
                            border: `1px solid ${BORDER}`,
                            "& .MuiChip-icon": {
                              color: THEME_ORANGE,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>{row.ProviderCode || "—"}</TableCell>
                      <TableCell>{row.DisplayName || "—"}</TableCell>
                      <TableCell>{row.ApiBaseUrl || "—"}</TableCell>
                      <TableCell>
                        {row.ApiKeyMasked || "********"}
                      </TableCell>
                      <TableCell>
                        {row.ApiSecretMasked || "********"}
                      </TableCell>
                      <TableCell>{row.SenderId || "—"}</TableCell>
                      <TableCell>{row.FromEmail || "—"}</TableCell>
                      <TableCell>{row.FromName || "—"}</TableCell>
                      <TableCell>
                        <Checkbox
                          checked={Boolean(row.IsActive)}
                          disabled
                          sx={{
                            p: 0.5,
                            color: BLUE,
                            "&.Mui-checked": { color: THEME_ORANGE },
                            "&.Mui-disabled": {
                              color: row.IsActive
                                ? `${THEME_ORANGE} !important`
                                : "rgba(25,118,210,0.35) !important",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={Boolean(row.IsDefault)}
                          disabled
                          sx={{
                            p: 0.5,
                            color: BLUE,
                            "&.Mui-checked": { color: THEME_ORANGE },
                            "&.Mui-disabled": {
                              color: row.IsDefault
                                ? `${THEME_ORANGE} !important`
                                : "rgba(25,118,210,0.35) !important",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {row.Channel === "telegram" && (
                          <Tooltip title="Register Telegram Webhook">
                            <IconButton
                              size="small"
                              sx={iconBtnSx}
                              onClick={() =>
                                registerTelegramWebhook(row)
                              }
                            >
                              <AppRegistrationIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title="Setup Guide">
                          <IconButton
                            size="small"
                            sx={iconBtnSx}
                            onClick={() =>
                              openSetupGuide(row.Channel)
                            }
                          >
                            <MenuBookIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            sx={iconBtnSx}
                            onClick={() => openEditProvider(row)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            sx={dangerIconBtnSx}
                            onClick={() => deleteProvider(row)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!providerLoading &&
                  paginatedProviders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={15}>
                        <Typography
                          sx={{
                            py: 4,
                            textAlign: "center",
                            color: "text.secondary",
                          }}
                        >
                          No notification provider configurations found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                <TableRow>
                  <TableCell
                    colSpan={15}
                    sx={{
                      p: 0,
                      borderBottom: 0,
                      backgroundColor: "rgba(25,118,210,.025)",
                    }}
                  >
                    <Button
                      fullWidth
                      startIcon={<AddIcon />}
                      onClick={openAddProvider}
                      sx={{
                        justifyContent: "center",
                        py: 1.35,
                        borderRadius: 0,
                        textTransform: "none",
                        fontWeight: 900,
                        color: THEME_ORANGE,
                        borderTop: `1px solid ${BORDER}`,
                        backgroundColor: "rgba(240,120,40,.035)",
                        "&:hover": {
                          backgroundColor:
                            "rgba(240,120,40,.085)",
                        },
                      }}
                    >
                      Add Notification Provider
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {providerLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        <TablePagination
          component="div"
          count={providerRows.length}
          page={safeProviderPage}
          onPageChange={(_, page) => setProviderPage(page)}
          rowsPerPage={providerRowsPerPage}
          onRowsPerPageChange={(e) => {
            setProviderRowsPerPage(
              parseInt(e.target.value, 10)
            );
            setProviderPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100, 200, 300]}
          labelRowsPerPage="Rows per page"
          showFirstButton
          showLastButton
        />
      </Paper>
    </Stack>
  );

  // ============================================================
  // PREFERENCES TAB
  // ============================================================

  const renderPreferences = () => (
    <Stack spacing={2}>
      <Paper elevation={0} sx={sectionSx}>
        <Typography sx={{ fontWeight: 900, color: BLUE }}>
          Event-wise Channel Selection
        </Typography>
        <Typography
          sx={{
            mt: 0.25,
            fontSize: "0.85rem",
            color: "text.secondary",
          }}
        >
          Provider configuration decides how to send. This tab decides what to send where.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 1450 }}>
              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell>#</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Kind</TableCell>
                  {PREFERENCE_CHANNELS.map((channel) => (
                    <TableCell
                      key={channel.key}
                      align="center"
                    >
                      <Stack
                        direction="row"
                        spacing={0.75}
                        justifyContent="center"
                        alignItems="center"
                      >
                        {channel.icon}
                        <span>{channel.label}</span>
                      </Stack>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedPreferences.map((row, index) => {
                  const serial =
                    safePreferencePage *
                      preferenceRowsPerPage +
                    index +
                    1;

                  return (
                    <TableRow
                      key={row.EventCode}
                      sx={zebraRowSx(index)}
                    >
                      <TableCell>{serialBadge(serial)}</TableCell>

                      <TableCell sx={{ minWidth: 240 }}>
                        <Typography
                          sx={{
                            fontWeight: 900,
                            color: BLUE,
                          }}
                        >
                          {row.EventName}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "text.secondary",
                          }}
                        >
                          {row.EventCode}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          label={row.DefaultKind}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            color: BLUE,
                            backgroundColor:
                              "rgba(25,118,210,0.08)",
                            border: `1px solid ${BORDER}`,
                          }}
                        />
                      </TableCell>

                      {PREFERENCE_CHANNELS.map((channel) => {
                        const state =
                          row.channels?.[channel.key] || {};

                        return (
                          <TableCell
                            key={channel.key}
                            align="center"
                            sx={{ minWidth: 150 }}
                          >
                            <Stack
                              spacing={0.4}
                              alignItems="center"
                            >
                              <Tooltip
                                title={`Enable ${channel.label} for this event`}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Checkbox
                                    checked={Boolean(
                                      state.IsEnabled
                                    )}
                                    onChange={() =>
                                      updatePreferenceChannel(
                                        row.EventCode,
                                        channel.key,
                                        "IsEnabled"
                                      )
                                    }
                                    sx={checkboxSx}
                                  />
                                  <Typography
                                    sx={{ fontSize: 12 }}
                                  >
                                    Enable
                                  </Typography>
                                </Box>
                              </Tooltip>

                              {channel.key === "telegram" && (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                >
                                  <Tooltip title="Send Telegram DM">
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={Boolean(
                                          state.SendDM
                                        )}
                                        onChange={() =>
                                          updatePreferenceChannel(
                                            row.EventCode,
                                            channel.key,
                                            "SendDM"
                                          )
                                        }
                                        disabled={
                                          !state.IsEnabled
                                        }
                                        sx={checkboxSx}
                                      />
                                      <Typography
                                        sx={{ fontSize: 11 }}
                                      >
                                        DM
                                      </Typography>
                                    </Box>
                                  </Tooltip>

                                  <Tooltip title="Post to Telegram channel">
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={Boolean(
                                          state.SendChannel
                                        )}
                                        onChange={() =>
                                          updatePreferenceChannel(
                                            row.EventCode,
                                            channel.key,
                                            "SendChannel"
                                          )
                                        }
                                        disabled={
                                          !state.IsEnabled
                                        }
                                        sx={checkboxSx}
                                      />
                                      <Typography
                                        sx={{ fontSize: 11 }}
                                      >
                                        Channel
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                </Stack>
                              )}
                            </Stack>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}

                {!preferenceLoading &&
                  paginatedPreferences.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography
                          sx={{
                            py: 4,
                            textAlign: "center",
                            color: "text.secondary",
                          }}
                        >
                          No notification events found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {preferenceLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        <TablePagination
          component="div"
          count={preferenceRows.length}
          page={safePreferencePage}
          onPageChange={(_, page) =>
            setPreferencePage(page)
          }
          rowsPerPage={preferenceRowsPerPage}
          onRowsPerPageChange={(e) => {
            setPreferenceRowsPerPage(
              parseInt(e.target.value, 10)
            );
            setPreferencePage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100, 200, 300]}
          labelRowsPerPage="Rows per page"
          showFirstButton
          showLastButton
        />
      </Paper>
    </Stack>
  );

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box
          sx={{
            ...headerSx,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "1.1rem",
                fontWeight: 900,
                lineHeight: 1.2,
              }}
            >
              Notification Setup
            </Typography>

            <Typography
              sx={{
                opacity: 0.9,
                fontSize: "0.85rem",
                mt: 0.25,
              }}
            >
              Configure notification providers and event-wise delivery channels from one page.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {activeTab.key === "providers" ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openAddProvider}
                  sx={{
                    ...primaryBtnSx,
                    backgroundColor: "white",
                    color: THEME_ORANGE,
                    "&:hover": {
                      backgroundColor:
                        "rgba(255,255,255,.92)",
                    },
                  }}
                >
                  Add Provider
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<MenuBookIcon />}
                  onClick={() =>
                    openSetupGuide(
                      providerDraft.Channel || "telegram"
                    )
                  }
                  sx={{
                    ...outlinedBtnSx,
                    backgroundColor: "white",
                  }}
                >
                  Setup Guide
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={seedMissingPreferences}
                  sx={{
                    ...outlinedBtnSx,
                    backgroundColor: "white",
                  }}
                >
                  Seed Missing
                </Button>

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={savePreferences}
                  disabled={preferenceSaving}
                  sx={{
                    ...primaryBtnSx,
                    backgroundColor: "white",
                    color: THEME_ORANGE,
                    "&:hover": {
                      backgroundColor:
                        "rgba(255,255,255,.92)",
                    },
                  }}
                >
                  Save Preferences
                </Button>
              </>
            )}

            <Chip
              icon={<SettingsIcon />}
              label={campusCode || "Campus"}
              sx={{
                color: "white",
                fontWeight: 800,
                border:
                  "1px solid rgba(255,255,255,0.45)",
                backgroundColor:
                  "rgba(255,255,255,0.14)",
                "& .MuiChip-icon": {
                  color: "white",
                },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ p: 2, bgcolor: BG }}>
          <Paper elevation={0} sx={surfaceSx}>
            <Tabs
              value={tab}
              onChange={(_, value) => {
                setTab(value);
                setProviderDialogOpen(false);
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={tabsSx}
            >
              {NOTIFICATION_TABS.map((item) => (
                <Tab
                  key={item.key}
                  label={item.label}
                  icon={item.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>

            <Box sx={{ p: 2 }}>
              {activeTab.key === "providers"
                ? renderProviders()
                : renderPreferences()}
            </Box>
          </Paper>
        </Box>
      </Paper>

      <Dialog
        open={providerDialogOpen}
        onClose={() =>
          !providerSaving && setProviderDialogOpen(false)
        }
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${BORDER}`,
            boxShadow:
              "0 18px 55px rgba(15,23,42,.22)",
            backgroundColor: "white",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              ...headerSx,
              borderRadius: 0,
              px: 3,
              py: 2,
            }}
          >
            <Typography sx={{ fontWeight: 900 }}>
              {providerMode === "add"
                ? "Add Notification Provider"
                : "Edit Notification Provider"}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.82rem",
                opacity: 0.9,
              }}
            >
              Configure provider credentials, sender settings and channel-specific options.
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            backgroundColor: BG,
            p: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ mt: 2 }}>
            {renderProviderForm()}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "white",
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          {providerDraft.Channel === "telegram" &&
            providerMode === "edit" && (
              <Button
                variant="outlined"
                startIcon={<AppRegistrationIcon />}
                sx={outlinedBtnSx}
                onClick={() =>
                  registerTelegramWebhook(
                    providerDraft
                  )
                }
              >
                Register Webhook
              </Button>
            )}

          <Box sx={{ flex: 1 }} />

          <Button
            startIcon={<CancelIcon />}
            onClick={() =>
              setProviderDialogOpen(false)
            }
            disabled={providerSaving}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveProvider}
            disabled={providerSaving}
            sx={primaryBtnSx}
          >
            {providerSaving
              ? "Saving..."
              : providerMode === "add"
              ? "Add Provider"
              : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(msg.text)}
        autoHideDuration={3500}
        onClose={() =>
          setMsg((prev) => ({
            ...prev,
            text: "",
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity={msg.type}
          onClose={() =>
            setMsg((prev) => ({
              ...prev,
              text: "",
            }))
          }
          sx={{ borderRadius: 2 }}
        >
          {msg.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
